import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { getRepositories } from "../../services/storage";
import { Ubicacion } from "../../types/interfaces";

interface UbicacionAutocompleteInputProps {
  value: string;
  onChange: (value: string) => void;
  onUbicacionIdChange: (ubicacionId: string | null, isUserAction?: boolean) => void;
  placeholder?: string;
  label?: string;
  required?: boolean;
  className?: string;
  error?: string;
  onBlur?: () => void;
  onFocus?: () => void;
  autoShowSuggestions?: boolean; // Whether to auto-show suggestions on focus
  /** Filtrar el catálogo por tipo de ubicación (ej: sólo corrales). */
  tiposPermitidos?: string[];
}

const UbicacionAutocompleteInput = forwardRef<HTMLInputElement, UbicacionAutocompleteInputProps>(
  (
    {
      value,
      onChange,
      onUbicacionIdChange,
      placeholder,
      label,
      required = false,
      className = "",
      error,
      onBlur,
      onFocus,
      autoShowSuggestions = true,
      tiposPermitidos,
    },
    ref
  ) => {
    const [ubicaciones, setUbicaciones] = useState<Ubicacion[]>([]);
    const [suggestions, setSuggestions] = useState<Ubicacion[]>([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isLoading, setIsLoading] = useState(false);
    const [hasExactMatch, setHasExactMatch] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionsRef = useRef<HTMLDivElement>(null);

    // Defensive contra callers inline: el callback se lee por ref (una arrow en
    // JSX cambia de identidad en CADA render del padre — tenerlo en los deps
    // del efecto de abajo re-disparaba onUbicacionIdChange en cada re-render,
    // y en callers con setState eso era un loop infinito de renders).
    const onUbicacionIdChangeRef = useRef(onUbicacionIdChange);
    useEffect(() => {
      onUbicacionIdChangeRef.current = onUbicacionIdChange;
    });
    // Deduplicación: sólo se emite cuando el id DERIVADO cambia — re-renders
    // del padre o recargas del catálogo no re-emiten el mismo valor.
    const lastEmittedIdRef = useRef<string | null | undefined>(undefined);
    const emitIdChange = (id: string | null, isUserAction = false) => {
      if (lastEmittedIdRef.current === id) return;
      lastEmittedIdRef.current = id;
      onUbicacionIdChangeRef.current(id, isUserAction);
    };

    // Forward the ref to the input element
    useImperativeHandle(ref, () => inputRef.current!, []);

    // Filtro por tipo estable por ref (el effect de carga corre una sola vez;
    // el prop es estático por instancia de formulario).
    const tiposPermitidosRef = useRef(tiposPermitidos);
    tiposPermitidosRef.current = tiposPermitidos;

    // Load ubicaciones on component mount
    useEffect(() => {
      const loadUbicaciones = async () => {
        try {
          // Sólo la empresa activa: el caché local acumula ubicaciones de todas
          // las empresas visitadas, y ofrecerlas al asignar genera referencias
          // cruzadas (animales con ubicacion_id de otra empresa).
          const allUbicaciones = await getRepositories().ubicacion.getAllForActiveEmpresa();
          const tipos = tiposPermitidosRef.current;
          setUbicaciones(
            tipos ? allUbicaciones.filter(u => tipos.includes(u.tipo)) : allUbicaciones
          );
        } catch (error) {
          console.error("Error loading ubicaciones:", error);
        }
      };
      loadUbicaciones();
    }, []);

    // Filter suggestions based on input value
    useEffect(() => {
      if (value.trim().length === 0) {
        // Show all ubicaciones when field is empty
        setSuggestions(ubicaciones);
        setShowSuggestions(false); // Don't show dropdown automatically when empty
        setHasExactMatch(false);
        emitIdChange(null);
        return;
      }

      const filtered = ubicaciones.filter(ubicacion =>
        ubicacion.nombre.toLowerCase().includes(value.toLowerCase())
      );
      setSuggestions(filtered);

      // Find exact match
      const exactMatch = filtered.find(ubicacion =>
        ubicacion.nombre.toLowerCase() === value.toLowerCase()
      );

      setHasExactMatch(!!exactMatch);

      // Show suggestions based on exact match:
      // - If there's an exact match: don't show suggestions (user has the right value)
      // - If there's no exact match but there are partial matches: show suggestions (user needs help)
      const shouldShow = !exactMatch && filtered.length > 0;

      setShowSuggestions(shouldShow);
      setHighlightedIndex(-1);

      emitIdChange(exactMatch ? exactMatch.id : null);
      // Nota: sin `onUbicacionIdChange` en los deps a propósito (ver ref arriba).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, ubicaciones]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!showSuggestions || suggestions.length === 0) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case "Enter":
          if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
            e.preventDefault();
            handleSuggestionSelect(suggestions[highlightedIndex]);
          }
          break;
        case "Escape":
          setShowSuggestions(false);
          setHighlightedIndex(-1);
          break;
      }
    };

    // Handle suggestion selection
    const handleSuggestionSelect = (ubicacion: Ubicacion) => {
      onChange(ubicacion.nombre);
      // Acción del usuario: se emite directo (y actualiza el dedupe para que el
      // efecto posterior con el mismo id no re-emita).
      lastEmittedIdRef.current = ubicacion.id;
      onUbicacionIdChangeRef.current(ubicacion.id, true);
      setShowSuggestions(false);
      setHighlightedIndex(-1);
      setSuggestions([]);
      inputRef.current?.blur();
    };

    // Handle input focus
    const handleFocus = () => {
      // Show all suggestions on focus if there are any, regardless of current input
      // This allows users to see all available options when they click on the field
      if (ubicaciones.length > 0) {
        // If no value, show all ubicaciones
        if (!value.trim()) {
          setSuggestions(ubicaciones);
          setShowSuggestions(true);
        } else {
          // If there's a value, show filtered suggestions (existing logic)
          if (suggestions.length > 0 && !hasExactMatch) {
            setShowSuggestions(true);
          }
        }
      }
      onFocus?.();
    };

    // Handle input blur with delay to allow clicking on suggestions
    const handleBlur = () => {
      setTimeout(() => {
        setShowSuggestions(false);
        setHighlightedIndex(-1);
      }, 150);
      onBlur?.();
    };

    // Handle clicks outside the component
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          inputRef.current &&
          !inputRef.current.contains(event.target as Node) &&
          suggestionsRef.current &&
          !suggestionsRef.current.contains(event.target as Node)
        ) {
          setShowSuggestions(false);
          setHighlightedIndex(-1);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);

    return (
      <div className="relative">
        {label && (
          <label className="block text-sm font-medium text-ink mb-1">
            {label}
            {required && <span className="text-danger ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={placeholder}
            className={`w-full rounded-md border bg-white px-3 py-2 text-sm text-ink placeholder:text-sage/70 focus:outline-none focus:ring-2 ${
              error
                ? "border-danger focus:border-danger focus:ring-danger/20"
                : "border-hairline focus:border-forest focus:ring-forest/20"
            } ${className}`}
            autoComplete="off"
            data-autocomplete-suggestions-visible={showSuggestions ? "true" : "false"}
          />

          {isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-forest"></div>
            </div>
          )}
        </div>

        {/* Suggestions Dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="absolute z-50 w-full mt-1 bg-paper border border-hairline rounded-md shadow-overlay max-h-48 overflow-y-auto"
          >
            {suggestions.map((ubicacion, index) => (
              <div
                key={ubicacion.id}
                className={`px-3 py-2 cursor-pointer text-sm hover:bg-paper-2 ${
                  index === highlightedIndex
                    ? "bg-forest/10 text-forest"
                    : "text-ink"
                }`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSuggestionSelect(ubicacion);
                }}
              >
                <div className="font-medium">{ubicacion.nombre}</div>
                <div className="text-xs text-sage">
                  Tipo: {ubicacion.tipo}
                  {ubicacion.hectareas && ` | ${ubicacion.hectareas} ha`}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && <p className="mt-1 text-sm text-danger">{error}</p>}
      </div>
    );
  }
);

UbicacionAutocompleteInput.displayName = "UbicacionAutocompleteInput";

export default UbicacionAutocompleteInput;